import { useState, useMemo, useCallback } from 'react';
import validate from './validate.esm.js';
import { getInitialValues, stateFromSchema, getInitialTouched, getInitialVisited, getValidationResult, getAllFieldsTouched } from './utils.esm.js';

/**
 * The `useController` hook allows the parent component to control all its state and, in that case, be
 * completely stateless, with validation being the only feature it adds to the low level StatelessForm.
 * It is also possible to opt-in only some of the data properties (values, errors, touched and visited)
 * to be stored by the component.
 *
 * NOTE: error messages in DEV to when the owner component changes one of the data properties between
 * controlled and uncontrolled.
 */

var useController = function useController(_ref) {
  var schema = _ref.schema,
      initialValues = _ref.initialValues,
      _ref$validateOnInit = _ref.validateOnInit,
      validateOnInit = _ref$validateOnInit === void 0 ? false : _ref$validateOnInit,
      additionalErrors = _ref.additionalErrors,
      _ref$valuesStateHook = _ref.valuesStateHook;
  _ref$valuesStateHook = _ref$valuesStateHook === void 0 ? useState(getInitialValues(schema, initialValues)) : _ref$valuesStateHook;
  var _ref$valuesStateHook$ = _ref$valuesStateHook[0],
      values = _ref$valuesStateHook$ === void 0 ? stateFromSchema(schema) : _ref$valuesStateHook$,
      baseSetValues = _ref$valuesStateHook[1],
      isNestedFormValues = _ref$valuesStateHook[2],
      _ref$touchedStateHook = _ref.touchedStateHook;
  _ref$touchedStateHook = _ref$touchedStateHook === void 0 ? useState(getInitialTouched(schema, validateOnInit)) : _ref$touchedStateHook;
  var _ref$touchedStateHook2 = _ref$touchedStateHook[0],
      touched = _ref$touchedStateHook2 === void 0 ? stateFromSchema(schema) : _ref$touchedStateHook2,
      baseSetTouched = _ref$touchedStateHook[1],
      isNestedFormTouched = _ref$touchedStateHook[2],
      _ref$visitedStateHook = _ref.visitedStateHook;
  _ref$visitedStateHook = _ref$visitedStateHook === void 0 ? useState(getInitialVisited(schema)) : _ref$visitedStateHook;
  var _ref$visitedStateHook2 = _ref$visitedStateHook[0],
      visited = _ref$visitedStateHook2 === void 0 ? stateFromSchema(schema) : _ref$visitedStateHook2,
      baseSetVisited = _ref$visitedStateHook[1],
      isNestedFormVisited = _ref$visitedStateHook[2],
      _ref$validate = _ref.validate,
      validate$1 = _ref$validate === void 0 ? validate : _ref$validate,
      _ref$validationResult = _ref.validationResult,
      validationResult = _ref$validationResult === void 0 ? getValidationResult(schema, values, touched, additionalErrors, validate$1) : _ref$validationResult,
      _ref$onFieldTouch = _ref.onFieldTouch,
      baseOnFieldTouch = _ref$onFieldTouch === void 0 ? function () {} : _ref$onFieldTouch,
      _ref$onFieldVisit = _ref.onFieldVisit,
      baseOnFieldVisit = _ref$onFieldVisit === void 0 ? function () {} : _ref$onFieldVisit,
      _ref$onChange = _ref.onChange,
      baseOnChange = _ref$onChange === void 0 ? function () {} : _ref$onChange,
      _ref$onSubmit = _ref.onSubmit,
      baseOnSubmit = _ref$onSubmit === void 0 ? function () {} : _ref$onSubmit,
      _ref$onFieldBlur = _ref.onFieldBlur,
      onFieldBlur = _ref$onFieldBlur === void 0 ? function () {} : _ref$onFieldBlur,
      _ref$onFieldFocus = _ref.onFieldFocus,
      onFieldFocus = _ref$onFieldFocus === void 0 ? function () {} : _ref$onFieldFocus;

  /**
   * Wrapper for setValues state updater to only bubble events on nested form changes.
   */
  var setValues = useMemo(function () {
    return isNestedFormValues ? baseSetValues : function (reducer) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      return baseSetValues.apply(void 0, [reducer].concat(args));
    };
  }, [baseSetValues, isNestedFormValues]);
  /**
   * Wrapper for setTouched state updater to only bubble events on nested form changes.
   */

  var setTouched = useMemo(function () {
    return isNestedFormTouched ? baseSetTouched : function (reducer) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      return baseSetTouched.apply(void 0, [reducer].concat(args));
    };
  }, [baseSetTouched, isNestedFormTouched]);
  /**
   * Wrapper for setVisited state updater to only bubble events on nested form changes.
   */

  var setVisited = useMemo(function () {
    return isNestedFormVisited ? baseSetVisited : function (reducer) {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }

      return baseSetVisited.apply(void 0, [reducer].concat(args));
    };
  }, [baseSetVisited, isNestedFormVisited]);
  /**
   * Form field touch handler.
   */

  var onFieldTouchedChange = useCallback(function (eventMetadata, reducer) {
    setTouched(reducer, eventMetadata);

    for (var _len4 = arguments.length, rest = new Array(_len4 > 2 ? _len4 - 2 : 0), _key4 = 2; _key4 < _len4; _key4++) {
      rest[_key4 - 2] = arguments[_key4];
    }

    baseOnFieldTouch.apply(void 0, [eventMetadata].concat(rest));
  }, [baseOnFieldTouch, setTouched]);
  /**
   * Form field visit handler.
   */

  var onFieldVisitedChange = useCallback(function (eventMetadata, reducer) {
    setVisited(reducer, eventMetadata);

    for (var _len5 = arguments.length, rest = new Array(_len5 > 2 ? _len5 - 2 : 0), _key5 = 2; _key5 < _len5; _key5++) {
      rest[_key5 - 2] = arguments[_key5];
    }

    baseOnFieldVisit.apply(void 0, [eventMetadata].concat(rest));
  }, [baseOnFieldVisit, setVisited]);
  /**
   * Form changes handler.
   */

  var onChange = useCallback(function (eventMetadata, reducer, _values) {
    setValues(reducer, eventMetadata);

    for (var _len6 = arguments.length, rest = new Array(_len6 > 3 ? _len6 - 3 : 0), _key6 = 3; _key6 < _len6; _key6++) {
      rest[_key6 - 3] = arguments[_key6];
    }

    baseOnChange.apply(void 0, [eventMetadata].concat(rest));
  }, [baseOnChange, setValues]);
  /**
   * Form submit handler.
   */

  var onSubmit = useMemo(function () {
    return (// Bypass by default when in a nested form.
      isNestedFormValues ? baseOnSubmit : function () {
        // When the submit method if called, we touch all fields to make them validatable.
        var touched = getAllFieldsTouched(schema);
        var validationResult = validate$1(schema, values, touched);

        if (validationResult[0]) {
          for (var _len7 = arguments.length, rest = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
            rest[_key7] = arguments[_key7];
          }

          baseOnSubmit.apply(void 0, [values].concat(rest));
        }

        setTouched(function () {
          return touched;
        });
        return validationResult;
      }
    );
  }, [isNestedFormValues, baseOnSubmit, schema, setTouched, values, validate$1]);
  return {
    schema: schema,
    onFieldBlur: onFieldBlur,
    onFieldFocus: onFieldFocus,
    values: values,
    touched: touched,
    visited: visited,
    validationResult: validationResult,
    onChange: onChange,
    onSubmit: onSubmit,
    onFieldTouchedChange: onFieldTouchedChange,
    onFieldVisitedChange: onFieldVisitedChange,
    // For lower level controlling, like performing state changes from inside the form
    // or performing batched updates without firing events.
    setValues: setValues,
    setTouched: setTouched,
    setVisited: setVisited
  };
};

export default useController;
//# sourceMappingURL=useController.esm.js.map
